import { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { Info } from 'lucide-react';
import { formatConnectorType, formatFileType, getNextCronDate } from '@Helpers';

import { Badge, Column, TableServer } from '@Components';
import { ClientObject, IntegrationFilterOptions, IntegrationType, Options } from '@Models';

interface IntegrationListProps {
  integrationsData: IntegrationType[];
  total: number;
  clientsObjects: ClientObject;
  selectedClients: Options[];
  selectedFileTypes: Options[];
  selectedConnectorTypes: Options[];
  pageSize: number;
  setTotal: (total: number) => void;
  search: string;
  onFilterOptionsUpdate: (options: IntegrationFilterOptions) => void;
}

const IntegrationList = ({
  integrationsData,
  total,
  clientsObjects,
  selectedClients,
  selectedFileTypes,
  selectedConnectorTypes,
  pageSize,
  setTotal,
  search,
  onFilterOptionsUpdate,
}: IntegrationListProps) => {
  const { t } = useTranslation();

  const getValueCell = (row: any, key: string): string | any[] | boolean => {
    let cellValue = row?.original[key];
    if (key === 'client_id') {
      const keyClient = row?.original?.client_id;
      cellValue = clientsObjects?.[keyClient] ?? keyClient;
    }
    if (key === 'file_last_updated') {
      if (!cellValue) return '';
      return moment(cellValue).format('DD/MM/YYYY HH:mm');
    }
    if (key === 'status') {
      if (row?.original?.ignore) return t('Integration.Ignore');
      if (!row?.original?.expected_file_cron) return t('Integration.OnTime');
      const scheduled = getNextCronDate(row?.original?.expected_file_cron);
      const scheduledDate = new Date(scheduled);
      const now = new Date();
      const sixHoursInMs = 6 * 60 * 60 * 1000;
      const deadline = new Date(scheduledDate.getTime() - sixHoursInMs);
      const lastUpdated = row?.original?.file_last_updated ? new Date(row?.original?.file_last_updated) : null;

      const isLate = scheduledDate <= now && (!lastUpdated || lastUpdated < deadline);
      return isLate ? t('Integration.Late') : t('Integration.OnTime');
    }
    if (key === 'expected_file_cron') {
      if (!row?.original?.expected_file_cron) return '';
      const scheduled = getNextCronDate(row?.original?.expected_file_cron);
      return moment.utc(scheduled).local().format('DD/MM/YYYY HH:mm');
    }
    if (key === 'file_type') {
      return formatFileType(cellValue);
    }
    if (key === 'connector_type') {
      return formatConnectorType(cellValue);
    }
    return cellValue;
  };

  const builderCell = (type: string, label: string, value: any): JSX.Element => {
    if (type === 'string' || (type === 'badge' && !value)) return <div key={label}>{value}</div>;
    if (type === 'path') {
      return <div className="w-[220px] 2xl:w-[400px]">{value}</div>;
    }
    if (type === 'badge' && value) {
      if (Array.isArray(value)) {
        return (
          <div className="flex gap-2 flex-wrap">
            {value.map((item: string, key: number) => (
              <Badge
                variant="gray"
                label={item.length > 20 ? `${item.slice(0, 20)}...` : item}
                key={item}
                tooltipId={`${key} ${item}`}
                toolInfo={item.length > 20 ? item : undefined}
              />
            ))}
          </div>
        );
      }
      return <Badge variant="gray" label={value} />;
    }

    if (type === 'badge-status') {
      return (
        <div className="w-[100px]">
          <Badge
            variant={value === t('Integration.Ignore') || value === t('Integration.Late') ? 'gray' : 'green'}
            label={value}
            showDot
          />
        </div>
      );
    }

    return <div key={label}>{value}</div>;
  };

  // Build filter query
  const filterQuery = useMemo(() => {
    const queryParts: string[] = [];

    if (selectedClients.length > 0) {
      queryParts.push(`client_id=${selectedClients.map((c) => c.value).join(',')}`);
    }
    if (selectedFileTypes.length > 0) {
      queryParts.push(`file_type=${selectedFileTypes.map((ft) => ft.value).join(',')}`);
    }
    if (selectedConnectorTypes.length > 0) {
      queryParts.push(`connector_type=${selectedConnectorTypes.map((ct) => ct.value).join(',')}`);
    }
    if (search) {
      queryParts.push(`path=${search}`);
    }

    return queryParts.join('&');
  }, [selectedClients, selectedFileTypes, selectedConnectorTypes, search]);

  const handleResponse = (response: any) => {
    if (response.filter_options) {
      onFilterOptionsUpdate(response.filter_options);
    }
  };

  const disabledSortColumns = ['file_last_updated', 'expected_file_cron', 'status'];

  const renderHeader = (label: string, accessor: string): JSX.Element | string => {
    if (disabledSortColumns.includes(accessor)) {
      const tooltipId = `sort-disabled-${accessor}`;
      return (
        <div className="flex items-center gap-1">
          <span>{label}</span>
          <span data-tooltip-id={tooltipId} className="cursor-default inline-flex items-center">
            <Info size={12} strokeWidth={2.5} />
          </span>
          <Tooltip id={tooltipId} content={t('Integration.SortNotAvailable')} place="top" />
        </div>
      );
    }
    return label;
  };

  const getColumns = (): Column[] => {
    const columns = [
      { label: t('Integration.Client'), accessor: 'client_id', key: 'client_id', type: 'string' },
      { label: t('Integration.Path'), accessor: 'path', key: 'path', type: 'path' },
      {
        label: t('Integration.LastUpdated'),
        accessor: 'file_last_updated',
        key: 'file_last_updated',
        type: 'date',
      },
      {
        label: t('Integration.NextCron'),
        accessor: 'expected_file_cron',
        key: 'expected_file_cron',
        type: 'string',
      },
      {
        label: t('Integration.FileType'),
        accessor: 'file_type',
        key: 'file_type',
        type: 'string',
      },
      { label: t('Integration.Status'), accessor: 'status', key: 'status', type: 'badge-status' },
      {
        label: t('Integration.Connector'),
        accessor: 'connector_type',
        key: 'connector_type',
        type: 'string',
      },
    ];

    const headers = columns.map((column) => {
      return {
        Header: renderHeader(column.label, column.accessor),
        accessor: column.accessor,
        Cell: ({ row }: { row: any }) => {
          const cellValue = getValueCell(row, column.key);
          return builderCell(column.type, column.label, cellValue);
        },
      };
    });
    return headers as Column[];
  };

  return (
    <TableServer
      sortable
      columns={getColumns()}
      dataResult={integrationsData}
      pageSize={pageSize}
      pagination
      total={total}
      setTotal={setTotal}
      filters={filterQuery}
      trackFilterOptions
      startPagination={1}
      endpoint="getIntegrations"
      indexPagination="items"
      incrementName="integrationsEvent"
      disableSortFields={['file_last_updated', 'expected_file_cron', 'status']}
      onResponse={handleResponse}
    />
  );
};

export default IntegrationList;
